import {
  StorageReference,
  getBlob,
  getMetadata,
  ref,
  listAll
} from "firebase/storage";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import { supportedExtensionsString } from "../constans";
import { storage } from "../api/firebaseApi";
import { sanitizeDownloadFilename } from "./downloadFilename";

// Get filename from firebase url
export const getFileName = (url: string | null | undefined) => {
  if (!url) return "";

  let decodedUrl: string;
  try {
    decodedUrl = url.includes("%") ? decodeURIComponent(url) : url;
  } catch {
    decodedUrl = url;
  }

  const segments = decodedUrl.split("/");
  const lastSegment = segments.pop() || "";
  return lastSegment.split("?")[0] || "Unknown Document";
};

export const downloadFileAsBlob = async (
  fileRef: StorageReference
): Promise<string> => {
  try {
    // Get the blob from the storage reference
    const blob = await getBlob(fileRef);

    // Create an object URL from the blob
    const objectURL = URL.createObjectURL(blob);

    return objectURL;
  } catch (error) {
    // Handle any errors that occur during the process
    Sentry.captureException(error);
    throw error;
  }
};

export const getFileFromUrl = async (url: string) => {
  try {
    const response = await fetch(url);

    // Check if the request was successful
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Get the file data
    const data = await response.blob();

    // Create a filename based on the URL or you could let the user specify one
    const filename = url.split("/").pop();

    // Create a new File object
    let file;
    if (filename) file = new File([data], filename, { type: data.type });

    return file;
  } catch (error) {
    Sentry.captureException(error);
  }
  return null;
};

export const getMimeType = (str: string) => {
  const prepared = str.split(";");
  const type = prepared[0].split(":");
  return type[1];
};

export const reader = (file: File) =>
  new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr);
    fr.onerror = (err) => reject(err);
    fr.readAsDataURL(file);
  });

// Extensions
export const handleUnsupportedExtension = (
  fileName: string,
  uploadInputRef?: React.RefObject<HTMLInputElement | null> | null
) => {
  const getFileExtension = (fileName?: string): string | undefined => {
    if (!fileName) {
      return undefined;
    }
    return fileName.split(".").pop()?.toLowerCase();
  };

  const isUnsupportedExtension = (fileExtension: string) => {
    const unsupportedExtensions = ["doc"];
    return unsupportedExtensions.includes(fileExtension);
  };

  const fileExtension = getFileExtension(fileName);
  if (fileExtension && isUnsupportedExtension(fileExtension)) {
    toast.warning("Templates must be .docx", {
      description: `'.${fileExtension}' templates can't be used here. Save the file as .docx in Word and try again. (Case files can still be .${fileExtension}; they open as a PDF preview.)`,
      duration: 5000
    });
    if (uploadInputRef?.current) {
      uploadInputRef.current.value = ""; // Clear the input
    }
    return true;
  }
  return false;
};

export const triggerDownload = (
  binaryData: ArrayBuffer,
  fileName: string,
  format: string
) => {
  const blob = new Blob([binaryData], { type: `application/${format}` });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = sanitizeDownloadFilename(fileName);
  document.body.appendChild(a);
  a.click();

  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const getFileExtension = (file: File) => {
  if (file && file.name) {
    return `.${file.name.split(".").pop()?.toLowerCase()}`;
  }
  return "";
};

export const filesHasUnsupportedExtension = (files: File[]) => {
  return files.some((file) => {
    const fileExtension = getFileExtension(file);
    return !supportedExtensionsString.includes(fileExtension ?? "");
  });
};

export const filterDocumentsBySupport = (files: File[], supported = true) => {
  let filteredFiles = [];

  filteredFiles = files.filter((file) => {
    if (file && file.name) {
      const fileExtension = getFileExtension(file);
      return supportedExtensionsString.includes(fileExtension) === supported;
    }
    return true;
  });

  return filteredFiles;
};

type FileUploadResult = {
  file: File;
  path: string;
};

export const createUniqueFileForUpload = async (
  path: string,
  file: File
): Promise<FileUploadResult> => {
  // If path includes filename, strip it to get directory
  const dirPath = path.includes(file.name)
    ? path.substring(0, path.lastIndexOf("/"))
    : path;

  const extension = file.name.substring(file.name.lastIndexOf("."));
  const nameWithoutExtension = file.name.substring(
    0,
    file.name.lastIndexOf(".")
  );
  let counter = 1;
  let newFileName = file.name;

  try {
    const dirRef = ref(storage, dirPath);
    const fileList = await listAll(dirRef);
    const existingFiles = new Set(fileList.items.map((item) => item.name));

    while (existingFiles.has(newFileName)) {
      newFileName = `${nameWithoutExtension} (${counter})${extension}`;
      counter += 1;
    }

    const newFile = new File([file], newFileName, {
      type: file.type,
      lastModified: file.lastModified
    });

    return {
      file: newFile,
      path: `${dirPath}/${newFileName}`
    };
  } catch (error) {
    Sentry.captureException(error);
    return {
      file,
      path: `${dirPath}/${file.name}`
    };
  }
};

export type PlaceholderFile = File & {
  placeholderDisplayName?: string;
  placeholderOriginalUrl?: string;
};

export const createPlaceholderFiles = (urls: string[]): PlaceholderFile[] => {
  const files: PlaceholderFile[] = [];
  urls.forEach((raw, idx) => {
    const trimmed = (raw || "").trim();
    if (!trimmed) return;

    const displayName = trimmed;
    let placeholderName = trimmed;

    try {
      const u = new URL(trimmed);
      const last = u.pathname.split("/").filter(Boolean).pop();
      placeholderName = last || u.hostname;
    } catch {
      placeholderName = `Link-${idx + 1}`;
    }

    if (!placeholderName) {
      placeholderName = `Link-${idx + 1}`;
    }

    let fileName = placeholderName;
    if (!fileName.toLowerCase().endsWith(".pdf")) {
      fileName = `${fileName}.pdf`;
    }

    const placeholderContent = "%PDF-1.4\n% placeholder";
    const placeholderFile = new File([placeholderContent], fileName, {
      type: "application/pdf"
    }) as PlaceholderFile;

    placeholderFile.placeholderDisplayName = displayName;
    placeholderFile.placeholderOriginalUrl = trimmed;

    files.push(placeholderFile);
  });
  return files;
};
